/** @module Starting a background run: one card each, saying what it does, asking before it fires. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { type Job, type Trigger, useTrigger } from "@/features/admin/use-admin";

interface Choice {
  value: string;
  trigger: Trigger;
}

interface Starter {
  key: string;
  /** The job type this produces, so a running one can grey the card out. */
  jobType: string;
  /** The first option is the default; a single entry means no choice to make. */
  choices: [Choice, ...Choice[]];
  /** True for the runs that take hours or put the machine under load. */
  heavy?: boolean;
}

const STARTERS: Starter[] = [
  {
    key: "catalog",
    jobType: "catalog_sync",
    choices: [
      { value: "incremental", trigger: { kind: "catalog", mode: "incremental" } },
      { value: "full", trigger: { kind: "catalog", mode: "full" } },
    ],
  },
  {
    key: "prices",
    jobType: "price_sync",
    choices: [{ value: "all", trigger: { kind: "prices" } }],
  },
  {
    key: "images",
    jobType: "image_prefetch",
    choices: [
      { value: "de", trigger: { kind: "images", quality: "high", lang: "de" } },
      { value: "en", trigger: { kind: "images", quality: "high", lang: "en" } },
      { value: "fr", trigger: { kind: "images", quality: "high", lang: "fr" } },
      { value: "it", trigger: { kind: "images", quality: "high", lang: "it" } },
      { value: "es", trigger: { kind: "images", quality: "high", lang: "es" } },
    ],
  },
  {
    key: "index",
    jobType: "index_build",
    choices: [
      { value: "incremental", trigger: { kind: "index", mode: "incremental" } },
      { value: "full", trigger: { kind: "index", mode: "full" } },
    ],
    heavy: true,
  },
  {
    key: "benchmark",
    jobType: "benchmark",
    choices: [{ value: "scans", trigger: { kind: "benchmark" } }],
    heavy: true,
  },
  {
    key: "backup",
    jobType: "backup",
    choices: [{ value: "now", trigger: { kind: "backup" } }],
  },
];

/** A run that is heavy, or a "full" variant of anything, is worth a second thought. */
function isHeavy(starter: Starter, choice: string): boolean {
  return Boolean(starter.heavy) || choice === "full";
}

function StarterCard({ starter, busy }: { starter: Starter; busy: boolean }) {
  const { t } = useTranslation();
  const trigger = useTrigger();
  const [choice, setChoice] = useState(starter.choices[0].value);
  const [asking, setAsking] = useState(false);
  const selected: Choice =
    starter.choices.find((entry) => entry.value === choice) ?? starter.choices[0];

  const fire = () => {
    setAsking(false);
    trigger.mutate(selected.trigger);
  };

  return (
    <li className="flex flex-col gap-2 panel panel-lg">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-medium">{t(`admin.jobs.starters.${starter.key}.name`)}</h4>
        <span className="t-xs t-muted">
          {t(`admin.jobs.starters.${starter.key}.duration.${choice}`)}
        </span>
      </div>
      <p className="t-sm t-muted">{t(`admin.jobs.starters.${starter.key}.what.${choice}`)}</p>

      {asking ? (
        <div className="flex flex-col gap-2 rounded-[var(--radius-sm)] border border-(--warn) p-3">
          <p className="text-[length:var(--text-sm)]">
            {t("admin.jobs.confirm", {
              name: t(`admin.jobs.starters.${starter.key}.name`),
              duration: t(`admin.jobs.starters.${starter.key}.duration.${choice}`),
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fire}
              className="min-h-(--touch) rounded-[var(--radius-pill)] bg-(--accent) px-5 font-medium text-(--accent-ink)"
            >
              {t("admin.jobs.confirmYes")}
            </button>
            <button
              type="button"
              onClick={() => {
                setAsking(false);
              }}
              className="btn btn-secondary"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {starter.choices.length > 1 ? (
            <select
              aria-label={t(`admin.jobs.starters.${starter.key}.name`)}
              value={choice}
              onChange={(event) => {
                setChoice(event.target.value);
              }}
              className="input text-[length:var(--text-sm)]"
            >
              {starter.choices.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {t(`admin.jobs.starters.${starter.key}.choices.${entry.value}`)}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            disabled={busy || trigger.isPending}
            onClick={() => {
              // Heavy runs ask first; the cheap ones would only be annoying.
              if (isHeavy(starter, choice)) setAsking(true);
              else fire();
            }}
            className="btn btn-secondary text-[length:var(--text-sm)] disabled:opacity-50"
          >
            {busy ? t("admin.jobs.alreadyRunning") : t("admin.jobs.startThis")}
          </button>
        </div>
      )}

      {trigger.isError ? (
        <p role="alert" className="t-sm loss">
          {trigger.error instanceof Error ? trigger.error.message : t("common.saveError")}
        </p>
      ) : null}
    </li>
  );
}

export function JobStarters({ running }: { running: Job[] }) {
  const { t } = useTranslation();
  const busy = new Set(running.map((job) => job.type));

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="t-h3">{t("admin.jobs.start")}</h3>
        <p className="t-sm t-muted">{t("admin.jobs.startHint")}</p>
      </div>
      <ul className="grid gap-3 lg:grid-cols-2">
        {STARTERS.map((starter) => (
          <StarterCard key={starter.key} starter={starter} busy={busy.has(starter.jobType)} />
        ))}
      </ul>
    </section>
  );
}
